// src/pages/AdminMonitoring.js - Supervision services critiques
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import './AdminMonitoring.css';

const API_URL = process.env.REACT_APP_API_URL;

function AdminMonitoring() {
  const [status, setStatus] = useState(null);
  const [stats, setStats] = useState(null);
  const [cronCheck, setCronCheck] = useState(null);
  const [stripeStats, setStripeStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [testingCron, setTestingCron] = useState(false);
  const [cronTestResult, setCronTestResult] = useState(null);

  const getAuthHeaders = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    return {
      'Authorization': `Bearer ${session?.access_token}`,
      'Content-Type': 'application/json'
    };
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const headers = await getAuthHeaders();

      if (!headers.Authorization.includes('null')) {
        // Fetch en parallèle
        const [statusRes, statsRes, cronRes, stripeRes] = await Promise.all([
          fetch(`${API_URL}/monitoring/status`, { headers }),
          fetch(`${API_URL}/monitoring/stats`, { headers }),
          fetch(`${API_URL}/monitoring/cron-check`, { headers }),
          fetch(`${API_URL}/monitoring/stripe-stats`, { headers }).catch(() => null)
        ]);

        if (!statusRes.ok || !statsRes.ok || !cronRes.ok) {
          throw new Error('Erreur lors de la récupération des données');
        }

        const [statusData, statsData, cronData] = await Promise.all([
          statusRes.json(),
          statsRes.json(),
          cronRes.json()
        ]);

        setStatus(statusData);
        setStats(statsData);
        setCronCheck(cronData);

        // Stripe stats (optionnel)
        if (stripeRes && stripeRes.ok) {
          const stripeData = await stripeRes.json();
          setStripeStats(stripeData);
        }

        setLastRefresh(new Date());
      } else {
        setError('Vous devez être connecté comme admin');
      }

    } catch (err) {
      console.error('Erreur monitoring:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    // Auto-refresh toutes les 60 secondes
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Test manuel du cron
  const testCronManual = async () => {
    if (!window.confirm('⚠️ Cela va recharger les crédits de TOUS les abonnements actifs. Continuer ?')) {
      return;
    }

    setTestingCron(true);
    setCronTestResult(null);

    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_URL}/admin/recharge-credits`, {
        method: 'POST',
        headers
      });

      const data = await response.json();

      if (response.ok) {
        setCronTestResult({
          success: true,
          message: `✅ Recharge effectuée ! ${data.abonnementsRecharges || 0} abonnement(s) rechargé(s).`
        });
        // Rafraîchir les données
        setTimeout(fetchData, 1000);
      } else {
        setCronTestResult({
          success: false,
          message: `❌ Erreur: ${data.error || 'Erreur inconnue'}`
        });
      }
    } catch (err) {
      setCronTestResult({
        success: false,
        message: `❌ Erreur: ${err.message}`
      });
    } finally {
      setTestingCron(false);
    }
  };

  // Envoyer alerte email test
  const sendTestAlert = async () => {
    if (!window.confirm('Envoyer un email de test à contact@firstarabic.com ?')) {
      return;
    }

    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_URL}/monitoring/send-test-alert`, {
        method: 'POST',
        headers
      });

      const data = await response.json();
      
      if (response.ok) {
        alert('✅ Email de test envoyé !');
      } else {
        alert(`❌ Erreur: ${data.error}`);
      }
    } catch (err) {
      alert(`❌ Erreur: ${err.message}`);
    }
  };

  const getStatusIcon = (serviceStatus) => {
    if (serviceStatus === 'ok') return '✅';
    if (serviceStatus === 'warning') return '⚠️';
    return '❌';
  };

  const getStatusClass = (serviceStatus) => {
    if (serviceStatus === 'ok') return 'status-ok';
    if (serviceStatus === 'warning') return 'status-warning';
    return 'status-error';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatRelativeTime = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'À l\'instant';
    if (diffMins < 60) return `Il y a ${diffMins} min`;
    if (diffHours < 24) return `Il y a ${diffHours}h`;
    return `Il y a ${diffDays} jour(s)`;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount / 100);
  };

  if (loading && !status) {
    return (
      <div className="admin-monitoring">
        <div className="loading-state">
          <span className="spinner">⏳</span>
          <p>Chargement du monitoring...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-monitoring">
        <div className="error-state">
          <span>❌</span>
          <p>{error}</p>
          <button onClick={fetchData}>Réessayer</button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-monitoring">
      {/* Header */}
      <header className="monitoring-header">
        <div className="header-left">
          <h1>📊 Monitoring Services</h1>
          <span className="last-refresh">
            Dernière MAJ: {lastRefresh ? formatDate(lastRefresh.toISOString()) : 'N/A'}
          </span>
        </div>
        <div className="header-actions">
          <button onClick={fetchData} className="btn-refresh" disabled={loading}>
            {loading ? '⏳' : '🔄'} Actualiser
          </button>
          <Link to="/admin" className="btn-back">← Dashboard</Link>
        </div>
      </header>

      <div className="monitoring-content">
        {/* Section Services Status */}
        <section className="monitoring-section">
          <h2>🔌 État des Services</h2>
          <div className="services-grid">
            {status?.services && Object.entries(status.services).map(([name, service]) => (
              <div key={name} className={`service-card ${getStatusClass(service.status)}`}>
                <div className="service-header">
                  <span className="service-icon">{getStatusIcon(service.status)}</span>
                  <h3>{name.charAt(0).toUpperCase() + name.slice(1)}</h3>
                </div>
                <div className="service-details">
                  <p><strong>Status:</strong> {service.status}</p>
                  {service.responseTime && (
                    <p><strong>Temps réponse:</strong> {service.responseTime}ms</p>
                  )}
                  {service.buckets && (
                    <p><strong>Buckets:</strong> {service.buckets.join(', ') || 'Aucun'}</p>
                  )}
                  {service.error && (
                    <p className="service-error"><strong>Erreur:</strong> {service.error}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Section Cron Job */}
        <section className="monitoring-section">
          <h2>⏰ Cron Job - Recharge Hebdomadaire</h2>
          {cronCheck && (
            <div className={`cron-status ${getStatusClass(cronCheck.status)}`}>
              <div className="cron-header">
                <span className="cron-icon">{getStatusIcon(cronCheck.status)}</span>
                <div>
                  <h3>{cronCheck.message}</h3>
                  <p>Prochain run: <strong>{formatDate(stats?.cron?.prochainRun)}</strong></p>
                </div>
              </div>
              
              <div className="cron-stats">
                <div className="cron-stat">
                  <span className="stat-value">{cronCheck.abonnementsActifs}</span>
                  <span className="stat-label">Abonnements actifs</span>
                </div>
                <div className="cron-stat">
                  <span className="stat-value text-green">{cronCheck.rechargesEffectuees}</span>
                  <span className="stat-label">Rechargés ✅</span>
                </div>
                <div className="cron-stat">
                  <span className="stat-value text-red">{cronCheck.rechargesManquantes}</span>
                  <span className="stat-label">Manquants ❌</span>
                </div>
              </div>

              {/* Bouton Test Manuel */}
              <div className="cron-actions">
                <button 
                  onClick={testCronManual} 
                  className="btn-test-cron"
                  disabled={testingCron}
                >
                  {testingCron ? '⏳ Test en cours...' : '🔄 Tester la recharge maintenant'}
                </button>
                {cronTestResult && (
                  <div className={`cron-test-result ${cronTestResult.success ? 'success' : 'error'}`}>
                    {cronTestResult.message}
                  </div>
                )}
              </div>

              {cronCheck.details && cronCheck.details.length > 0 && (
                <div className="cron-details">
                  <h4>Détail des abonnements</h4>
                  <table>
                    <thead>
                      <tr>
                        <th>Type</th>
                        <th>Crédits</th>
                        <th>Dernière recharge</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cronCheck.details.map((detail, idx) => (
                        <tr key={idx}>
                          <td>{detail.type}</td>
                          <td>{detail.credits}</td>
                          <td>{formatRelativeTime(detail.derniereRecharge)}</td>
                          <td>{detail.rechargeOK ? '✅' : '❌'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </section>

        {/* Section Stats Stripe */}
        <section className="monitoring-section">
          <h2>💳 Statistiques Stripe</h2>
          {stripeStats ? (
            <div className="stripe-stats">
              <div className="stats-grid">
                <div className="stat-card stripe-card">
                  <span className="stat-icon">💰</span>
                  <div>
                    <span className="stat-value">{formatCurrency(stripeStats.revenusMois || 0)}</span>
                    <span className="stat-label">Revenus ce mois</span>
                  </div>
                </div>
                <div className="stat-card stripe-card">
                  <span className="stat-icon">✅</span>
                  <div>
                    <span className="stat-value">{stripeStats.paiementsReussis || 0}</span>
                    <span className="stat-label">Paiements réussis</span>
                  </div>
                </div>
                <div className="stat-card stripe-card">
                  <span className="stat-icon">❌</span>
                  <div>
                    <span className="stat-value text-red">{stripeStats.paiementsEchoues || 0}</span>
                    <span className="stat-label">Paiements échoués</span>
                  </div>
                </div>
                <div className="stat-card stripe-card">
                  <span className="stat-icon">🔄</span>
                  <div>
                    <span className="stat-value">{stripeStats.abonnementsActifs || 0}</span>
                    <span className="stat-label">Abonnements Stripe actifs</span>
                  </div>
                </div>
              </div>

              {stripeStats.derniersPaiements && stripeStats.derniersPaiements.length > 0 && (
                <div className="stripe-recent">
                  <h4>Derniers paiements</h4>
                  <table>
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Montant</th>
                        <th>Status</th>
                        <th>Client</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stripeStats.derniersPaiements.map((paiement, idx) => (
                        <tr key={idx}>
                          <td>{formatDate(paiement.created * 1000)}</td>
                          <td>{formatCurrency(paiement.amount)}</td>
                          <td>{paiement.status === 'succeeded' ? '✅' : '❌'}</td>
                          <td>{paiement.customer_email || 'N/A'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ) : (
            <div className="stripe-placeholder">
              <p>📊 Les stats Stripe seront disponibles après configuration.</p>
              <a href="https://dashboard.stripe.com" target="_blank" rel="noopener noreferrer" className="external-link">
                <span>💳</span> Voir Dashboard Stripe
              </a>
            </div>
          )}
        </section>

        {/* Section Alertes Email */}
        <section className="monitoring-section">
          <h2>🚨 Alertes Email</h2>
          <div className="alerts-config">
            <p>Les alertes sont envoyées automatiquement à <strong>contact@firstarabic.com</strong> en cas de :</p>
            <ul>
              <li>❌ Service down (Database, Auth, Storage)</li>
              <li>⚠️ Cron job échoué (recharge non effectuée)</li>
              <li>💳 Paiement Stripe échoué</li>
            </ul>
            <button onClick={sendTestAlert} className="btn-test-alert">
              📧 Envoyer un email de test
            </button>
          </div>
        </section>

        {/* Section Stats BDD */}
        <section className="monitoring-section">
          <h2>🗄️ Statistiques Base de Données</h2>
          {stats && (
            <div className="stats-grid">
              <div className="stat-card">
                <span className="stat-icon">👨‍🎓</span>
                <div>
                  <span className="stat-value">{stats.database?.eleves || 0}</span>
                  <span className="stat-label">Élèves</span>
                </div>
              </div>
              <div className="stat-card">
                <span className="stat-icon">👨‍🏫</span>
                <div>
                  <span className="stat-value">{stats.database?.profs || 0}</span>
                  <span className="stat-label">Professeurs</span>
                </div>
              </div>
              <div className="stat-card">
                <span className="stat-icon">📅</span>
                <div>
                  <span className="stat-value">{stats.database?.reservations || 0}</span>
                  <span className="stat-label">Réservations</span>
                </div>
              </div>
              <div className="stat-card">
                <span className="stat-icon">💳</span>
                <div>
                  <span className="stat-value">{stats.database?.abonnementsActifs || 0}</span>
                  <span className="stat-label">Abonnements actifs</span>
                </div>
              </div>
            </div>
          )}
        </section>

        {/* Section Activité */}
        <section className="monitoring-section">
          <h2>📈 Activité</h2>
          {stats && (
            <div className="activity-grid">
              <div className="activity-card">
                <div className="activity-header">
                  <span>📧</span>
                  <h4>Messages non lus</h4>
                </div>
                <span className={`activity-value ${stats.activity?.messagesNonLus > 0 ? 'text-orange' : ''}`}>
                  {stats.activity?.messagesNonLus || 0}
                </span>
                <Link to="/admin/messages">Voir les messages →</Link>
              </div>
              <div className="activity-card">
                <div className="activity-header">
                  <span>📅</span>
                  <h4>Réservations aujourd'hui</h4>
                </div>
                <span className="activity-value">{stats.activity?.reservationsAujourdhui || 0}</span>
              </div>
              <div className="activity-card">
                <div className="activity-header">
                  <span>🆕</span>
                  <h4>Inscriptions (7 jours)</h4>
                </div>
                <span className="activity-value">{stats.activity?.inscriptionsSemaine || 0}</span>
              </div>
            </div>
          )}
        </section>

        {/* Section Liens Externes */}
        <section className="monitoring-section">
          <h2>🔗 Liens Dashboards Externes</h2>
          <div className="external-links">
            <a href="https://vercel.com/dashboard" target="_blank" rel="noopener noreferrer" className="external-link">
              <span>▲</span> Vercel
            </a>
            <a href="https://supabase.com/dashboard" target="_blank" rel="noopener noreferrer" className="external-link">
              <span>⚡</span> Supabase
            </a>
            <a href="https://dashboard.stripe.com" target="_blank" rel="noopener noreferrer" className="external-link">
              <span>💳</span> Stripe
            </a>
            <a href="https://resend.com/emails" target="_blank" rel="noopener noreferrer" className="external-link">
              <span>📧</span> Resend
            </a>
            <a href="https://cron-job.org" target="_blank" rel="noopener noreferrer" className="external-link">
              <span>⏰</span> Cron-job.org
            </a>
            <a href="https://console.agora.io" target="_blank" rel="noopener noreferrer" className="external-link">
              <span>📹</span> Agora
            </a>
          </div>
        </section>
      </div>
    </div>
  );
}

export default AdminMonitoring;
